import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { flag } from '../utils/countryFlags';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2, 
  CheckCircle, 
  XCircle,
  User,
  Phone,
  Mail,
  MapPin,
  Plane,
  Calendar,
  Package,
  RefreshCw,
  FileText,
  AlertTriangle,
  Info,
  Ticket
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Statuts d'annonces
const LISTING_STATUS_LABELS = {
  pending_verification: 'En approbation',
  active: 'Active',
  reserved: 'Réservée',
  closed: 'Fermée',
  expired: 'Expirée',
  canceled: 'Annulée',
  rejected: 'Rejetée'
};

const LISTING_STATUS_COLORS = {
  pending_verification: 'bg-orange-500/20 text-orange-500',
  active: 'bg-green-500/20 text-green-500',
  reserved: 'bg-amber-500/20 text-amber-500',
  closed: 'bg-gray-500/20 text-gray-500',
  expired: 'bg-gray-500/20 text-gray-500',
  canceled: 'bg-red-500/20 text-red-500',
  rejected: 'bg-red-500/20 text-red-500'
};

const AdminListingsManager = ({ onDataChange, initialSellerFilter }) => {
  const { token, API } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    departure_country: '',
    destination_country: '',
    seller_name: initialSellerFilter || '',
    seller_matricule: ''
  });
  const [selectedListing, setSelectedListing] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSellerDialog, setShowSellerDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [editData, setEditData] = useState({});
  const [ticketData, setTicketData] = useState(null);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [listingOrders, setListingOrders] = useState([]);
  const [approvalDialog, setApprovalDialog] = useState({ open: false, listing: null });
  const [ticketCheckDialog, setTicketCheckDialog] = useState({ open: false, listing: null });
  const [ticketRejectDialog, setTicketRejectDialog] = useState({ open: false, listing: null, reason: '' });

  useEffect(() => {
    fetchListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Réagir quand initialSellerFilter change depuis le parent
  useEffect(() => {
    if (initialSellerFilter !== undefined && initialSellerFilter !== null) {
      setFilters(prev => ({ ...prev, seller_name: initialSellerFilter }));
      // Auto-search after filter is set
      setTimeout(fetchListings, 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSellerFilter]);

  // Déclencher automatiquement la recherche quand le filtre de statut change
  useEffect(() => {
    if (filters.status !== undefined) {
      fetchListings();
    }
  }, [filters.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.departure_country) params.append('departure_country', filters.departure_country);
      if (filters.destination_country) params.append('destination_country', filters.destination_country);
      if (filters.seller_name) params.append('seller_name', filters.seller_name);
      if (filters.seller_matricule) params.append('seller_matricule', filters.seller_matricule);

      const res = await axios.get(`${API}/admin/listings/all?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListings(res.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
      toast.error('Erreur de chargement des annonces');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchListings();
  };

  const loadTicket = async (listingId) => {
    setTicketLoading(true);
    setTicketData(null);
    try {
      const res = await axios.get(`${API}/listings/${listingId}/ticket`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const contentType = res.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([res.data], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);
      setTicketData({ url: blobUrl, type: contentType });
    } catch {
      setTicketData(null);
    } finally {
      setTicketLoading(false);
    }
  };

  const handleResetFilters = () => {
    setFilters({
      status: '',
      departure_country: '',
      destination_country: '',
      seller_name: '',
      seller_matricule: ''
    });
    // Refetch after reset
    setTimeout(fetchListings, 100);
  };

  const handleVerify = async (listing) => {
    setApprovalDialog({ open: true, listing });
    if (listing.has_ticket) {
      loadTicket(listing.id);
    } else {
      setTicketData(null);
    }
  };

  const confirmApproval = async (approveWithoutTicket) => {
    const listing = approvalDialog.listing;
    if (!listing) return;
    try {
      await axios.post(`${API}/admin/listings/${listing.id}/verify`, { approve_without_ticket: approveWithoutTicket }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(approveWithoutTicket ? 'Annonce approuvée sans billet' : 'Annonce approuvée');
      setApprovalDialog({ open: false, listing: null });
      setTicketData(null);
      fetchListings();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast.error("Erreur lors de l'approbation");
    }
  };

  const openTicketCheck = (listing) => {
    setTicketCheckDialog({ open: true, listing });
    if (listing.has_ticket) {
      loadTicket(listing.id);
    } else {
      setTicketData(null);
    }
  };

  const handleVerifyTicket = async () => {
    const listing = ticketCheckDialog.listing;
    if (!listing) return;
    try {
      await axios.post(`${API}/admin/listings/${listing.id}/verify-ticket`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Billet vérifié');
      setTicketCheckDialog({ open: false, listing: null });
      setTicketData(null);
      fetchListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleRejectTicket = async () => {
    const listing = ticketRejectDialog.listing;
    if (!listing || !ticketRejectDialog.reason.trim()) {
      toast.error('La raison est obligatoire');
      return;
    }
    try {
      await axios.post(`${API}/admin/listings/${listing.id}/reject-ticket`, { reason: ticketRejectDialog.reason.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Billet rejeté - Vendeur notifié');
      setTicketRejectDialog({ open: false, listing: null, reason: '' });
      setTicketCheckDialog({ open: false, listing: null });
      setTicketData(null);
      fetchListings();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingListing, setRejectingListing] = useState(null);

  const openRejectDialog = (listing) => {
    setRejectingListing(listing);
    setRejectReason('');
    setShowRejectDialog(true);
  };

  const handleReject = async () => {
    if (!rejectingListing) return;
    try {
      await axios.post(`${API}/admin/listings/${rejectingListing.id}/reject`, 
        { reason: rejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Annonce rejetée - Email envoyé au vendeur');
      setShowRejectDialog(false);
      setRejectingListing(null);
      setRejectReason('');
      fetchListings();
    } catch (error) {
      toast.error('Erreur lors du rejet');
    }
  };

  const handleDelete = async () => {
    if (!selectedListing) return;
    try {
      await axios.delete(`${API}/admin/listings/${selectedListing.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Annonce supprimée définitivement');
      setShowDeleteDialog(false);
      setSelectedListing(null);
      fetchListings();
      // Rafraîchir aussi les données du dashboard parent (notamment la section Annulées)
      if (onDataChange) {
        onDataChange();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleUpdate = async () => {
    if (!selectedListing) return;
    try {
      await axios.put(`${API}/admin/listings/${selectedListing.id}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Annonce mise à jour');
      setShowEditDialog(false);
      setSelectedListing(null);
      fetchListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const openEditDialog = (listing) => {
    setSelectedListing(listing);
    setEditData({
      status: listing.status,
      departure_city: listing.departure_city,
      destination_city: listing.destination_city,
      travel_date: listing.travel_date,
      price_per_unit: listing.price_per_unit,
      quantity: listing.quantity,
      available_quantity: listing.available_quantity
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (listing) => {
    setSelectedListing(listing);
    setShowDeleteDialog(true);
  };

  const openSellerDialog = (listing) => {
    setSelectedListing(listing);
    setShowSellerDialog(true);
  };

  const openDetailDialog = (listing) => {
    setSelectedListing(listing);
    setShowDetailDialog(true);
    setListingOrders([]);
    if (listing.has_ticket) {
      loadTicket(listing.id);
    } else {
      setTicketData(null);
    }
    // Load related orders
    axios.get(`${API}/admin/listings/${listing.id}/orders`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setListingOrders(res.data?.orders || []))
    .catch(() => setListingOrders([]));
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtres de recherche
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <Label className="text-xs">Statut</Label>
              <Select value={filters.status || "all"} onValueChange={(v) => setFilters({...filters, status: v === "all" ? "" : v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  {Object.entries(LISTING_STATUS_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Pays de départ</Label>
              <Input
                placeholder="Ex: France"
                value={filters.departure_country}
                onChange={(e) => setFilters({...filters, departure_country: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-xs">Pays de destination</Label>
              <Input
                placeholder="Ex: Cameroun"
                value={filters.destination_country}
                onChange={(e) => setFilters({...filters, destination_country: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-xs">Nom du vendeur</Label>
              <Input
                placeholder="Nom ou prénom"
                value={filters.seller_name}
                onChange={(e) => setFilters({...filters, seller_name: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-xs">Matricule</Label>
              <Input
                placeholder="Ex: A1B2C3D4"
                value={filters.seller_matricule}
                onChange={(e) => setFilters({...filters, seller_matricule: e.target.value.toUpperCase()})}
              />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={handleSearch} className="flex-1">
                <Search className="w-4 h-4 mr-1" />
                Rechercher
              </Button>
              <Button variant="outline" onClick={handleResetFilters}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {listings.length} annonce(s) trouvée(s)
        </p>
        <Button variant="outline" size="sm" onClick={fetchListings} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Listings Table */}
      <div className="space-y-3">
        {loading ? (
          <Card>
            <CardContent className="py-12 text-center">
              <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin text-muted-foreground" />
              <p className="text-muted-foreground">Chargement...</p>
            </CardContent>
          </Card>
        ) : listings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">Aucune annonce trouvée</p>
            </CardContent>
          </Card>
        ) : (
          listings.map((listing) => (
            <Card key={listing.id} className="hover:border-primary/30 transition-colors">
              <CardContent className="py-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Listing Info */}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={LISTING_STATUS_COLORS[listing.status] || 'bg-gray-500/20 text-gray-500'}>
                        {LISTING_STATUS_LABELS[listing.status] || listing.status}
                      </Badge>
                      {listing.has_ticket && (
                        <Badge variant="outline" className="text-green-500 border-green-500/30">
                          Billet uploadé
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">ID: {listing.id.substring(0, 8)}...</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Plane className="w-4 h-4 text-primary" />
                      <span className="font-medium">
                        {flag(listing.departure_country)} {listing.departure_city} ({listing.departure_country}) → {flag(listing.destination_country)} {listing.destination_city} ({listing.destination_country})
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(listing.travel_date + 'T12:00:00').toLocaleDateString('fr-FR')}
                      </span>
                      <span>{listing.airline}</span>
                      <span>{listing.available_quantity}/{listing.quantity} dispo</span>
                      <span className="text-primary font-medium">{listing.price_per_unit?.toFixed(2)} {listing.currency || 'CAD'}</span>
                    </div>
                  </div>

                  {/* Seller Info */}
                  {listing.seller_info && (
                    <div 
                      className="bg-muted/50 p-3 rounded-lg cursor-pointer hover:bg-muted transition-colors min-w-[200px]"
                      onClick={() => openSellerDialog(listing)}
                    >
                      <p className="text-xs text-muted-foreground mb-1">Vendeur</p>
                      <p className="font-medium">{listing.seller_info.full_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Matricule: {listing.seller_info.matricule}
                      </p>
                      <div className="flex items-center gap-1 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {listing.seller_info.grade}
                        </Badge>
                        {listing.seller_info.profile_verified && (
                          <CheckCircle className="w-3 h-3 text-green-500" />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    {/* Pending verification without ticket warning */}
                    {listing.status === 'pending_verification' && !listing.has_ticket && (
                      <Badge className="bg-yellow-500/20 text-yellow-600 justify-center">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        En attente de billet
                      </Badge>
                    )}
                    
                    {/* Active without ticket warning */}
                    {listing.status === 'active' && !listing.has_ticket && (
                      <Badge className="bg-orange-500/20 text-orange-600 justify-center">
                        <FileText className="w-3 h-3 mr-1" />
                        Approuvée sans billet
                      </Badge>
                    )}
                    
                    {/* View Details button */}
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="w-full"
                      onClick={() => openDetailDialog(listing)}
                      data-testid={`view-details-${listing.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Vue en détails
                    </Button>
                    
                    {/* Verify/Reject buttons for pending status */}
                    {listing.status === 'pending_verification' && (
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="bg-green-500 hover:bg-green-600 flex-1"
                          onClick={() => handleVerify(listing)}
                          data-testid={`verify-${listing.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approuver
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          className="flex-1"
                          onClick={() => openRejectDialog(listing)}
                          data-testid={`reject-${listing.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Rejeter
                        </Button>
                      </div>
                    )}
                    
                    {/* Edit/Delete + Verify Ticket for all statuses */}
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openTicketCheck(listing)}
                        data-testid={`check-ticket-${listing.id}`}
                        title="Vérifier le billet"
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openEditDialog(listing)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-500 hover:text-red-600"
                        onClick={() => openDeleteDialog(listing)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'annonce</DialogTitle>
            <DialogDescription>
              Modifiez les informations de l'annonce
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Statut</Label>
              <Select value={editData.status} onValueChange={(v) => setEditData({...editData, status: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LISTING_STATUS_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ville de départ</Label>
                <Input
                  value={editData.departure_city || ''}
                  onChange={(e) => setEditData({...editData, departure_city: e.target.value})}
                />
              </div>
              <div>
                <Label>Ville de destination</Label>
                <Input
                  value={editData.destination_city || ''}
                  onChange={(e) => setEditData({...editData, destination_city: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Date de voyage</Label>
              <Input
                type="date"
                value={editData.travel_date || ''}
                onChange={(e) => setEditData({...editData, travel_date: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Prix/unité</Label>
                <Input
                  type="number"
                  value={editData.price_per_unit || ''}
                  onChange={(e) => setEditData({...editData, price_per_unit: parseFloat(e.target.value)})}
                />
              </div>
              <div>
                <Label>Quantité totale</Label>
                <Input
                  type="number"
                  value={editData.quantity || ''}
                  onChange={(e) => setEditData({...editData, quantity: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <Label>Disponible</Label>
                <Input
                  type="number"
                  value={editData.available_quantity || ''}
                  onChange={(e) => setEditData({...editData, available_quantity: parseInt(e.target.value)})}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Annuler</Button>
            <Button onClick={handleUpdate}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer cette annonce ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          {selectedListing && (
            <div className="py-4">
              <p className="font-medium">
                {flag(selectedListing.departure_country)} {selectedListing.departure_city} → {flag(selectedListing.destination_country)} {selectedListing.destination_city}
              </p>
              <p className="text-sm text-muted-foreground">
                {new Date(selectedListing.travel_date + 'T12:00:00').toLocaleDateString('fr-FR')}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Annuler</Button>
            <Button variant="destructive" onClick={handleDelete}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seller Info Dialog */}
      <Dialog open={showSellerDialog} onOpenChange={setShowSellerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Informations du vendeur</DialogTitle>
          </DialogHeader>
          {selectedListing?.seller_info && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{selectedListing.seller_info.full_name}</h3>
                  <p className="text-sm text-muted-foreground">Matricule: {selectedListing.seller_info.matricule}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge>{selectedListing.seller_info.grade}</Badge>
                    <span className="text-sm">Score: {selectedListing.seller_info.trust_score}/100</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{selectedListing.seller_info.email}</p>
                  </div>
                  {selectedListing.seller_info.email_verified && (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Téléphone</p>
                    <p className="font-medium">{selectedListing.seller_info.phone}</p>
                  </div>
                  {selectedListing.seller_info.phone_verified && (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Pays</p>
                    <p className="font-medium">{selectedListing.seller_info.country}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Membre depuis</p>
                    <p className="font-medium">
                      {selectedListing.seller_info.created_at 
                        ? new Date(selectedListing.seller_info.created_at).toLocaleDateString('fr-FR')
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Badge variant={selectedListing.seller_info.profile_verified ? "default" : "outline"}>
                  {selectedListing.seller_info.profile_verified ? '✓ Profil vérifié' : 'Profil non vérifié'}
                </Badge>
                <Badge variant={selectedListing.seller_info.email_verified ? "default" : "outline"}>
                  {selectedListing.seller_info.email_verified ? '✓ Email vérifié' : 'Email non vérifié'}
                </Badge>
                <Badge variant={selectedListing.seller_info.phone_verified ? "default" : "outline"}>
                  {selectedListing.seller_info.phone_verified ? '✓ Tél. vérifié' : 'Tél. non vérifié'}
                </Badge>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSellerDialog(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Detail View Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={(open) => {
        if (!open && ticketData?.url?.startsWith('blob:')) {
          URL.revokeObjectURL(ticketData.url);
          setTicketData(null);
        }
        setShowDetailDialog(open);
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="w-5 h-5 text-primary" />
              Détails complets de l'annonce
            </DialogTitle>
            <DialogDescription>
              Toutes les informations pour vous aider à prendre une décision
            </DialogDescription>
          </DialogHeader>
          
          {selectedListing && (
            <Tabs defaultValue="listing" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="listing">Annonce</TabsTrigger>
                <TabsTrigger value="ticket">Billet</TabsTrigger>
                <TabsTrigger value="seller">Vendeur</TabsTrigger>
              </TabsList>
              
              {/* Listing Tab */}
              <TabsContent value="listing" className="space-y-4 mt-4">
                {/* Status and ID */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={LISTING_STATUS_COLORS[selectedListing.status] || 'bg-gray-500/20 text-gray-500'}>
                    {LISTING_STATUS_LABELS[selectedListing.status] || selectedListing.status}
                  </Badge>
                  {selectedListing.has_ticket ? (
                    <Badge className="bg-green-500/20 text-green-500">
                      <Ticket className="w-3 h-3 mr-1" />
                      Billet uploadé
                    </Badge>
                  ) : (
                    <Badge className="bg-yellow-500/20 text-yellow-500">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Billet non uploadé
                    </Badge>
                  )}
                  <span className="text-xs text-muted-foreground">ID: {selectedListing.id}</span>
                </div>
                
                {/* Route */}
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="text-center">
                        <p className="text-2xl font-bold">{selectedListing.departure_city}</p>
                        <p className="text-sm text-muted-foreground">{selectedListing.departure_country}</p>
                      </div>
                      <Plane className="w-8 h-8 text-primary mx-4" />
                      <div className="text-center">
                        <p className="text-2xl font-bold">{selectedListing.destination_city}</p>
                        <p className="text-sm text-muted-foreground">{selectedListing.destination_country}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Flight Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Date de voyage</p>
                    <p className="font-medium text-lg">
                      {new Date(selectedListing.travel_date + 'T12:00:00').toLocaleDateString('fr-FR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Compagnie aérienne</p>
                    <p className="font-medium text-lg">{selectedListing.airline || 'Non spécifiée'}</p>
                  </div>
                </div>
                
                {/* Baggage Info */}
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2">Bagages disponibles</p>
                  {selectedListing.baggage_items && selectedListing.baggage_items.length > 0 ? (
                    <div className="space-y-2">
                      {selectedListing.baggage_items.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 bg-background rounded">
                          <span className="font-medium capitalize">{item.type}</span>
                          <span>{item.available_quantity}/{item.total_quantity} disponibles</span>
                          <span className="text-primary font-bold">{item.price_per_unit?.toFixed(2)} {selectedListing.currency || 'CAD'}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{selectedListing.baggage_type || 'Kilos'}</span>
                      <span>{selectedListing.available_quantity}/{selectedListing.quantity} disponibles</span>
                      <span className="text-primary font-bold">{selectedListing.price_per_unit?.toFixed(2)} {selectedListing.currency || 'CAD'}</span>
                    </div>
                  )}
                </div>
                
                {/* Pickup/Delivery Cities */}
                {(selectedListing.pickup_cities?.length > 0 || selectedListing.delivery_cities?.length > 0) && (
                  <div className="grid grid-cols-2 gap-4">
                    {selectedListing.pickup_cities?.length > 0 && (
                      <div className="p-4 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground mb-2">Villes de collecte</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedListing.pickup_cities.map((city, idx) => (
                            <Badge key={idx} variant="outline">{city}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedListing.delivery_cities?.length > 0 && (
                      <div className="p-4 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground mb-2">Villes de livraison</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedListing.delivery_cities.map((city, idx) => (
                            <Badge key={idx} variant="outline">{city}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Notes */}
                {selectedListing.notes && (
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground mb-2">Notes du vendeur</p>
                    <p className="text-sm">{selectedListing.notes}</p>
                  </div>
                )}
                
                {/* Creation date */}
                <div className="text-xs text-muted-foreground text-right">
                  Créée le {new Date(selectedListing.created_at).toLocaleDateString('fr-FR')} à {new Date(selectedListing.created_at).toLocaleTimeString('fr-FR')}
                </div>
              </TabsContent>
              
              {/* Ticket Tab */}
              <TabsContent value="ticket" className="space-y-4 mt-4">
                {!selectedListing.has_ticket ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Ticket className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Aucun billet uploadé pour cette annonce</p>
                  </div>
                ) : ticketLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <span className="ml-3">Chargement du billet...</span>
                  </div>
                ) : ticketData ? (
                  <div className="space-y-4">
                    {ticketData.type?.startsWith('image') ? (
                      <img src={ticketData.url} alt="Billet d'avion" className="max-w-full rounded-lg border" />
                    ) : ticketData.type?.includes('pdf') ? (
                      <div className="space-y-3">
                        <iframe src={ticketData.url} className="w-full h-[500px] rounded-lg border" title="Billet PDF" />
                        <a href={ticketData.url} download="billet.pdf" className="inline-flex items-center gap-2 text-primary hover:underline text-sm">
                          <FileText className="w-4 h-4" /> Télécharger le PDF
                        </a>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <a href={ticketData.url} download="billet" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:opacity-90">
                          <FileText className="w-4 h-4" /> Télécharger le fichier
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Impossible de charger le billet</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => loadTicket(selectedListing.id)}>
                      <RefreshCw className="w-4 h-4 mr-1" /> Réessayer
                    </Button>
                  </div>
                )}
              </TabsContent>
              
              {/* Seller Tab */}
              <TabsContent value="seller" className="space-y-4 mt-4">
                {selectedListing.seller_info ? (
                  <>
                    {/* Seller Header */}
                    <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                      <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-10 h-10 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold">{selectedListing.seller_info.full_name}</h3>
                        <p className="text-muted-foreground">Matricule: <span className="font-mono">{selectedListing.seller_info.matricule}</span></p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className="text-lg px-3 py-1">{selectedListing.seller_info.grade}</Badge>
                          <span className="text-sm">Score de confiance: <strong>{selectedListing.seller_info.trust_score}/100</strong></span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Contact Info */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Coordonnées</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <div className="flex items-center gap-3">
                            <Mail className="w-5 h-5 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground">Email</p>
                              <p className="font-medium">{selectedListing.seller_info.email}</p>
                            </div>
                          </div>
                          <Badge variant={selectedListing.seller_info.email_verified ? "default" : "destructive"}>
                            {selectedListing.seller_info.email_verified ? '✓ Vérifié' : '✗ Non vérifié'}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <div className="flex items-center gap-3">
                            <Phone className="w-5 h-5 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground">Téléphone</p>
                              <p className="font-medium">{selectedListing.seller_info.phone}</p>
                            </div>
                          </div>
                          <Badge variant={selectedListing.seller_info.phone_verified ? "default" : "destructive"}>
                            {selectedListing.seller_info.phone_verified ? '✓ Vérifié' : '✗ Non vérifié'}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                          <MapPin className="w-5 h-5 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Pays de résidence</p>
                            <p className="font-medium">{selectedListing.seller_info.country}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Verification Status */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Statut de vérification</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-3">
                          <div className={`p-3 rounded-lg text-center ${selectedListing.seller_info.profile_verified ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                            <p className={`font-medium ${selectedListing.seller_info.profile_verified ? 'text-green-500' : 'text-red-500'}`}>
                              {selectedListing.seller_info.profile_verified ? '✓' : '✗'}
                            </p>
                            <p className="text-xs mt-1">Profil</p>
                          </div>
                          <div className={`p-3 rounded-lg text-center ${selectedListing.seller_info.email_verified ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                            <p className={`font-medium ${selectedListing.seller_info.email_verified ? 'text-green-500' : 'text-red-500'}`}>
                              {selectedListing.seller_info.email_verified ? '✓' : '✗'}
                            </p>
                            <p className="text-xs mt-1">Email</p>
                          </div>
                          <div className={`p-3 rounded-lg text-center ${selectedListing.seller_info.phone_verified ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                            <p className={`font-medium ${selectedListing.seller_info.phone_verified ? 'text-green-500' : 'text-red-500'}`}>
                              {selectedListing.seller_info.phone_verified ? '✓' : '✗'}
                            </p>
                            <p className="text-xs mt-1">Téléphone</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Member Since */}
                    <p className="text-xs text-muted-foreground text-right">
                      Membre depuis le {selectedListing.seller_info.created_at 
                        ? new Date(selectedListing.seller_info.created_at).toLocaleDateString('fr-FR')
                        : 'N/A'}
                    </p>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <User className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Informations du vendeur non disponibles</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
            {selectedListing && selectedListing.status === 'pending_verification' && (
              <>
                <Button 
                  className="bg-green-500 hover:bg-green-600"
                  onClick={() => {
                    handleVerify(selectedListing.id);
                    setShowDetailDialog(false);
                  }}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Approuver l'annonce
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => {
                    setShowDetailDialog(false);
                    openRejectDialog(selectedListing);
                  }}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Rejeter l'annonce
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Fermer</Button>
          </DialogFooter>

          {/* Related Orders */}
          {listingOrders.length > 0 && (
            <div className="mt-4 border-t pt-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Commandes liées ({listingOrders.length})
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {listingOrders.map(order => {
                  const sLabels = {
                    pending_admin_validation: 'Attente validation',
                    pending_payment: 'Attente paiement',
                    paid: 'Payé', in_transit: 'En transit',
                    delivered: 'Livré', completed: 'Complété',
                    cancelled: 'Annulé', rejected: 'Refusé'
                  };
                  const sColors = {
                    pending_admin_validation: 'bg-amber-500/20 text-amber-600',
                    pending_payment: 'bg-yellow-500/20 text-yellow-500',
                    paid: 'bg-amber-500/20 text-amber-500',
                    delivered: 'bg-green-500/20 text-green-500',
                    completed: 'bg-emerald-500/20 text-emerald-500',
                    cancelled: 'bg-red-500/20 text-red-500',
                    rejected: 'bg-red-500/20 text-red-500'
                  };
                  return (
                    <div key={order.id} className="p-3 border rounded-lg flex items-center justify-between gap-2 text-sm">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={sColors[order.current_status] || 'bg-gray-500/20 text-gray-500'}>
                            {sLabels[order.current_status] || order.current_status}
                          </Badge>
                          <span className="text-xs text-muted-foreground">#{order.id?.substring(0, 8).toUpperCase()}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {order.buyer_info?.full_name || order.buyer_name || 'N/A'} • {order.quantity} {order.baggage_type === 'kilos' ? 'kg' : 'unité(s)'} • {order.total?.toFixed(2)} {order.currency || 'CAD'}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowDetailDialog(false);
                          navigate(`/admin?search=${order.id?.substring(0, 8)}`);
                        }}
                        data-testid={`listing-order-${order.id}`}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" />Détails
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Listing Dialog with Reason */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <XCircle className="w-5 h-5" />
              Rejeter l'annonce
            </DialogTitle>
            <DialogDescription>
              Un email sera envoyé au vendeur pour l'informer du rejet et de la raison.
            </DialogDescription>
          </DialogHeader>
          {rejectingListing && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">
                  {flag(rejectingListing.departure_country)} {rejectingListing.departure_city} → {flag(rejectingListing.destination_country)} {rejectingListing.destination_city}
                </p>
                <p className="text-sm text-muted-foreground">
                  {new Date(rejectingListing.travel_date + 'T12:00:00').toLocaleDateString('fr-FR')} • {rejectingListing.airline}
                </p>
                {rejectingListing.seller_info && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Vendeur: {rejectingListing.seller_info.full_name}
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="reject-reason" className="text-sm font-medium">
                  Justification du rejet *
                </Label>
                <Textarea
                  id="reject-reason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Ex: Le billet d'avion n'est pas lisible, les dates ne correspondent pas, informations manquantes..."
                  className="w-full mt-2 min-h-[120px] text-foreground bg-background"
                  data-testid="reject-reason-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Cette justification sera incluse dans l'email envoyé au vendeur.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowRejectDialog(false);
              setRejectingListing(null);
              setRejectReason('');
            }}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={!rejectReason.trim()}
            >
              <XCircle className="w-4 h-4 mr-1" />
              Rejeter l'annonce
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog - shows ticket before approving */}
      <Dialog open={approvalDialog.open} onOpenChange={(open) => { if (!open) { setApprovalDialog({ open: false, listing: null }); setTicketData(null); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Approuver l'annonce</DialogTitle>
            <DialogDescription>
              Vérifiez le billet d'avion avant d'approuver
            </DialogDescription>
          </DialogHeader>
          {approvalDialog.listing && (
            <div className="space-y-4 py-2">
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium">{flag(approvalDialog.listing.departure_country)} {approvalDialog.listing.departure_city} → {flag(approvalDialog.listing.destination_country)} {approvalDialog.listing.destination_city}</p>
                <p className="text-muted-foreground">{new Date(approvalDialog.listing.travel_date + 'T12:00:00').toLocaleDateString('fr-FR')} • {approvalDialog.listing.airline}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">Billet d'avion</Label>
                <div className="mt-2 border rounded-lg p-3 min-h-[120px] flex items-center justify-center">
                  {ticketLoading ? (
                    <p className="text-sm text-muted-foreground">Chargement...</p>
                  ) : ticketData ? (
                    <div className="w-full">
                      {ticketData.type?.includes('pdf') ? (
                        <div className="text-center">
                          <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                          <a href={ticketData.url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">Ouvrir le PDF</a>
                        </div>
                      ) : (
                        <img src={ticketData.url} alt="Billet" className="max-w-full max-h-[300px] rounded mx-auto" />
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-amber-500 font-medium">Aucun billet téléchargé pour le moment</p>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setApprovalDialog({ open: false, listing: null }); setTicketData(null); }}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={() => { setApprovalDialog({ open: false, listing: null }); if (approvalDialog.listing) openRejectDialog(approvalDialog.listing); }}>
              <XCircle className="w-4 h-4 mr-1" />Rejeter
            </Button>
            <Button className="bg-green-500 hover:bg-green-600" onClick={() => confirmApproval(!ticketData)} data-testid="confirm-approval-btn">
              <CheckCircle className="w-4 h-4 mr-1" />{ticketData ? 'Approuver' : 'Approuver sans billet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Check Dialog */}
      <Dialog open={ticketCheckDialog.open} onOpenChange={(open) => { if (!open) { setTicketCheckDialog({ open: false, listing: null }); setTicketData(null); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vérifier le billet</DialogTitle>
            {ticketCheckDialog.listing && (
              <DialogDescription>
                {flag(ticketCheckDialog.listing.departure_country)} {ticketCheckDialog.listing.departure_city} → {flag(ticketCheckDialog.listing.destination_country)} {ticketCheckDialog.listing.destination_city}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="py-2">
            <div className="border rounded-lg p-3 min-h-[120px] flex items-center justify-center">
              {ticketLoading ? (
                <p className="text-sm text-muted-foreground">Chargement...</p>
              ) : ticketData ? (
                <div className="w-full">
                  {ticketData.type?.includes('pdf') ? (
                    <div className="text-center">
                      <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                      <a href={ticketData.url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">Ouvrir le PDF</a>
                    </div>
                  ) : (
                    <img src={ticketData.url} alt="Billet" className="max-w-full max-h-[300px] rounded mx-auto" />
                  )}
                </div>
              ) : (
                <p className="text-sm text-amber-500 font-medium">Aucun billet téléchargé pour le moment</p>
              )}
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setTicketCheckDialog({ open: false, listing: null }); setTicketData(null); }}>
              Fermer
            </Button>
            {ticketData && (
              <>
                <Button variant="destructive" onClick={() => setTicketRejectDialog({ open: true, listing: ticketCheckDialog.listing, reason: '' })}>
                  <XCircle className="w-4 h-4 mr-1" />Rejeter billet
                </Button>
                <Button className="bg-green-500 hover:bg-green-600" onClick={handleVerifyTicket}>
                  <CheckCircle className="w-4 h-4 mr-1" />Valider billet
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Reject Reason Dialog */}
      <Dialog open={ticketRejectDialog.open} onOpenChange={(open) => { if (!open) setTicketRejectDialog({ open: false, listing: null, reason: '' }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-500">Rejeter le billet</DialogTitle>
            <DialogDescription>Le vendeur sera notifié par email avec la raison du rejet.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="ticket-reject-reason">Raison du rejet *</Label>
            <Textarea
              id="ticket-reject-reason"
              value={ticketRejectDialog.reason}
              onChange={(e) => setTicketRejectDialog(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Ex: Billet illisible, dates ne correspondent pas..."
              className="mt-2 min-h-[100px]"
              data-testid="ticket-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTicketRejectDialog({ open: false, listing: null, reason: '' })}>Annuler</Button>
            <Button variant="destructive" onClick={handleRejectTicket} disabled={!ticketRejectDialog.reason.trim()}>
              <XCircle className="w-4 h-4 mr-1" />Confirmer le rejet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminListingsManager;
